import React, { useState } from 'react';
import axios from 'axios';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  Spin, 
  Space, 
  Layout,
  Tabs
} from 'antd';
import { 
  YoutubeOutlined, 
  MailOutlined, 
  SendOutlined, 
  ClockCircleOutlined,
  SyncOutlined 
} from '@ant-design/icons';
import {API_BASE_URL} from '../../utils/constants';
import { ProcessVideoRequest, ApiResponse, ScheduleChannelRequest } from '../../types/youtubeSummarizer';

const { Title, Paragraph } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;

const YouTubeSummarizer: React.FC = () => {
  const [form] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingVideos, setCheckingVideos] = useState<boolean>(false);
  const [result, setResult] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: ProcessVideoRequest): Promise<void> => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post<ApiResponse>(API_BASE_URL + '/transcript/process', {
        videoUrl: values.videoUrl,
        email: values.email
      });
      setResult(response.data);
      form.resetFields();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data?.message || 'An error occurred while processing the video');
      } else {
        setError('An unexpected error occurred');
      }
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChannel = async (values: ScheduleChannelRequest): Promise<void> => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Update this to use the actual API endpoint
      const response = await axios.post<ApiResponse>(API_BASE_URL + '/transcript/schedule-channel', {
        channelId: values.channelId,
        email: values.email
      });

      setResult(response.data);
      scheduleForm.resetFields();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data?.message || 'An error occurred while scheduling the channel');
      } else {
        setError('An unexpected error occurred');
      }
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckNewVideos = async (): Promise<void> => {
    setCheckingVideos(true);
    setError(null);
    setResult(null);

    // Get the email from the form
    const email = scheduleForm.getFieldValue('email');

    if (!email) {
      setError('Please enter an email address to check for new videos');
      setCheckingVideos(false);
      return;
    }

    try {
      const response = await axios.get<ApiResponse>(
        `${API_BASE_URL}/transcript/check-new-videos?email=${encodeURIComponent(email)}`
      );
      setResult(response.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data?.message || 'An error occurred while checking for new videos');
      } else {
        setError('An unexpected error occurred');
      }
      console.error('Error:', err);
    } finally {
      setCheckingVideos(false);
    }
  };


  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '50px 50px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <Card
            title={
              <Space>
                <YoutubeOutlined style={{ fontSize: '24px', color: '#ff0000' }} />
                <Title level={2} style={{ margin: 0 }}>YouTube Video Summarizer</Title>
              </Space>
            }
            bordered={false}
            style={{ boxShadow: '0 1px 2px -2px rgba(0, 0, 0, 0.16), 0 3px 6px 0 rgba(0, 0, 0, 0.12), 0 5px 12px 4px rgba(0, 0, 0, 0.09)' }}
          >
            <Tabs defaultActiveKey="summarize">
              <TabPane
                tab={
                  <span>
                    <SendOutlined />
                    Summarize Video
                  </span>
                }
                key="summarize"
              >
                <Paragraph>
                  Enter a YouTube video URL and your email address to receive a summarized transcript of the video.
                </Paragraph>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  autoComplete="off"
                >
                  <Form.Item
                    name="videoUrl"
                    label="YouTube Video URL"
                    rules={[
                      { required: true, message: 'Please enter a YouTube video URL' },
                      {
                        pattern: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/,
                        message: 'Please enter a valid YouTube URL'
                      }
                    ]}
                  >
                    <Input
                      prefix={<YoutubeOutlined style={{ color: '#ff0000' }} />}
                      placeholder="https://www.youtube.com/watch?v=..."
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: 'Please enter your email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined />}
                      placeholder="your@email.com"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SendOutlined />}
                        loading={loading}
                        size="large"
                        block
                      >
                        Summarize Video
                      </Button>


                    </Space>
                  </Form.Item>
                </Form>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <ClockCircleOutlined />
                    Schedule Channel
                  </span>
                }
                key="schedule"
              >
                <Paragraph>
                  Enter a YouTube channel ID and your email address to automatically receive summaries of new videos from this channel.
                </Paragraph>

                <Form
                  form={scheduleForm}
                  layout="vertical"
                  onFinish={handleScheduleChannel}
                  autoComplete="off"
                >
                  <Form.Item
                    name="channelId"
                    label="YouTube Channel ID"
                    rules={[
                      { required: true, message: 'Please enter a YouTube channel ID' },
                      {
                        pattern: /^UC[\w-]{21}[AQgw]$/,
                        message: 'Please enter a valid YouTube channel ID (starts with UC)'
                      }
                    ]}
                    tooltip="You can find the channel ID in the URL of the channel page or in the channel's about section"
                  >
                    <Input
                      prefix={<YoutubeOutlined style={{ color: '#ff0000' }} />}
                      placeholder="UCxxxxxxxxxxxxxxxxxxxxxxx"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: 'Please enter your email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined />}
                      placeholder="your@email.com"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<ClockCircleOutlined />}
                      loading={loading}
                      size="large"
                      block
                    >
                      Schedule Channel
                    </Button>

                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="default"
                      icon={<SyncOutlined />}
                      onClick={handleCheckNewVideos}
                      loading={checkingVideos}
                      size="large"
                      block
                    >
                      Check For New Videos
                    </Button>
                  </Form.Item>

                </Form>


              </TabPane>
            </Tabs>

            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            {result && (
              <Alert
                message="Success"
                description={
                  <div>
                    <p>{result.message}</p>
                    {result.videoId && (
                      <>
                        <p><strong>Video ID:</strong> {result.videoId}</p>
                        <p><strong>Title:</strong> {result.title}</p>
                      </>
                    )}
                  </div>
                }
                type="success"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            {(loading || checkingVideos) && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Spin size="large" />
                <p style={{ marginTop: 8 }}>
                  {loading ? 'Processing your request...' : 'Checking for new videos...'}
                </p>
              </div>
            )}
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default YouTubeSummarizer;